import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Search,
  FileText,
  Key,
  Settings,
  LogOut,
  Shield,
  Menu,
  X,
  Users,
  Webhook,
  ShieldCheck,
  Database,
  Building2,
  CreditCard,
  AlertTriangle,
  ClipboardList,
  BarChart3,
  Inbox,
  Archive
} from 'lucide-react';

import AlertNotifications from '@/components/AlertNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Layout({ children, user, onLogout, activePage }) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [creditWarning, setCreditWarning] = useState(null);

  // Fetch credit status for org users
  useEffect(() => {
    if (!user?.organization_id || user?.role === 'super_admin') return;
    
    const fetchCredits = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/organizations/${user.organization_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const org = await res.json();
        const total = org.prepaid_credits || 0;
        const remaining = org.remaining_credits || 0;
        const pct = total > 0 ? (remaining / total) * 100 : 0;

        if (pct <= 10) {
          setCreditWarning({ level: 'critical', remaining, total, pct });
        } else if (pct <= 25) {
          setCreditWarning({ level: 'warning', remaining, total, pct });
        } else {
          setCreditWarning(null);
        }
      } catch {
        // silently fail
      }
    };
    fetchCredits();
  }, [user?.organization_id, user?.role]);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, key: 'dashboard', roles: ['super_admin', 'org_admin', 'admin', 'analyst', 'viewer'] },
    { name: 'Scan', href: '/scan', icon: Search, key: 'scan', roles: ['super_admin', 'org_admin', 'admin', 'analyst'] },
    { name: 'Results', href: '/results', icon: FileText, key: 'results', roles: ['super_admin', 'org_admin', 'admin', 'analyst', 'viewer'] },
    { name: 'API Keys', href: '/integrations', icon: Key, key: 'api-keys', roles: ['super_admin'] },
    { name: 'Webhooks', href: '/webhooks', icon: Webhook, key: 'webhooks', roles: ['super_admin'] },
    { name: 'Organizations', href: '/organizations', icon: Building2, key: 'organizations', roles: ['super_admin'] },
    { name: 'My Org', href: '/org-portal', icon: Building2, key: 'org-portal', roles: ['org_admin', 'analyst', 'viewer'] },
    { name: 'Billing', href: '/billing', icon: CreditCard, key: 'billing', roles: ['org_admin', 'analyst', 'viewer'] },
    { name: 'Data', href: '/data', icon: Database, key: 'data', roles: ['super_admin'] },
    { name: 'Audit Logs', href: '/audit-logs', icon: ClipboardList, key: 'audit-logs', roles: ['super_admin', 'org_admin'] },
    { name: 'Compliance Archive', href: '/compliance-archive', icon: Archive, key: 'compliance-archive', roles: ['super_admin', 'org_admin', 'admin', 'analyst'] },
    { name: 'Usage', href: '/usage', icon: BarChart3, key: 'usage', roles: ['super_admin', 'org_admin'] },
    { name: 'Users', href: '/users', icon: Users, key: 'users', roles: ['super_admin', 'org_admin', 'admin'] },
    { name: 'Demo Requests', href: '/demo-requests', icon: Inbox, key: 'demo-requests', roles: ['super_admin'] },
    { name: 'Settings', href: '/settings', icon: Settings, key: 'settings', roles: ['super_admin'] },
    { name: 'Security', href: '/security', icon: ShieldCheck, key: 'security', roles: ['super_admin', 'org_admin', 'admin', 'analyst', 'viewer'] }
  ];

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item => 
    item.roles.includes(user?.role || 'viewer')
  );

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      super_admin: { label: 'Super Admin', variant: 'default', className: 'bg-purple-600' },
      org_admin: { label: 'Org Admin', variant: 'default', className: 'bg-blue-600' },
      admin: { label: 'Admin', variant: 'default', className: 'bg-blue-600' },
      analyst: { label: 'Analyst', variant: 'secondary' },
      viewer: { label: 'Viewer', variant: 'outline' }
    };
    const config = roleConfig[role] || roleConfig.viewer;
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-md flex items-center justify-center mr-3 shadow-sm">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900 tracking-tight">My Shield</h1>
                <p className="text-xs text-slate-500">Compliance Screening Platform</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1" data-testid="desktop-nav">
              {filteredNavigation.map((item) => {
                const isActive = activePage === item.key;
                return (
                  <Link key={item.key} to={item.href} data-testid={`nav-link-${item.key}`}>
                    <Button
                      variant="ghost"
                      className={`text-sm ${
                        isActive
                          ? 'bg-slate-100 text-slate-900 font-medium'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Button>
                  </Link>
                );
              })}
            </nav>

            {/* Alerts + User Menu */}
            <div className="flex items-center space-x-3">
              {user?.session_timeout_minutes && (
                <span
                  className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-medium text-slate-500 tracking-wide"
                  title={`You will be signed out after ${user.session_timeout_minutes} minutes of inactivity.`}
                  data-testid="session-timeout-badge"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Idle logout · {user.session_timeout_minutes}m
                </span>
              )}
              <AlertNotifications />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center space-x-2"
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white text-sm">
                        {user?.full_name?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium text-slate-900">
                      {user?.full_name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>
                    <div className="space-y-1">
                      <p className="font-medium text-slate-900">{user?.full_name}</p>
                      <p className="text-xs text-slate-500 font-normal">{user?.email}</p>
                      <div className="pt-1">{getRoleBadge(user?.role)}</div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4" data-testid="mobile-nav">
              {filteredNavigation.map((item) => {
                const isActive = activePage === item.key;
                return (
                  <Link key={item.key} to={item.href} onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className={`w-full justify-start mb-1 ${
                        isActive
                          ? 'bg-slate-100 text-slate-900 font-medium'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Button>
                  </Link>
                );
              })}
            </nav>
          )}
        </div>
      </header>

      {/* Low-Credit Alert Banner */}
      {creditWarning && (
        <div
          data-testid="low-credit-banner"
          className={`px-6 py-2.5 flex items-center justify-between text-sm ${
            creditWarning.level === 'critical'
              ? 'bg-rose-50 border-b border-rose-200 text-rose-800'
              : 'bg-amber-50 border-b border-amber-200 text-amber-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">
              {creditWarning.level === 'critical' ? 'Credits Nearly Depleted' : 'Low Credits'}
            </span>
            <span className="hidden sm:inline">
              — {creditWarning.remaining.toLocaleString()} of {creditWarning.total.toLocaleString()} remaining ({creditWarning.pct.toFixed(0)}%)
            </span>
          </div>
          <Link to="/billing" data-testid="low-credit-billing-link">
            <Button
              size="sm"
              variant="outline"
              className={creditWarning.level === 'critical'
                ? 'border-rose-300 text-rose-700 hover:bg-rose-100'
                : 'border-amber-300 text-amber-700 hover:bg-amber-100'
              }
            >
              View Billing
            </Button>
          </Link>
        </div>
      )}

      {/* Main Content */}
      <main className="p-6 md:p-8 lg:p-12">{children}</main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-slate-500">
            © 2026 My Shield. Powered by OFAC & OpenSanctions.
          </p>
        </div>
      </footer>
    </div>
  );
}